// File: /components/Shufflemaps/Dropdowns/ListingDropdown/ListingDropdown.jsx
"use client";

import React, { useEffect, useRef, useState, useId, useCallback } from "react";
import Link from "next/link";
import styles from "./ListingDropdown.module.css";

/**
 * ListingDropdown
 * - Fixed label: "Listings"
 * - Items:
 *    • "My Listings" → /ShuffleMaps/MyListings
 *    • "Create Listing" → /ShuffleMaps/CreateListing
 * - Caret removed (clean text-only toggle)
 *
 * Props:
 *   className?: string
 *   onCreateClick?: (event: MouseEvent) => void
 *   onMyListingsClick?: (event: MouseEvent) => void   // optional, not required
 */
export default function ListingDropdown({ className = "", onCreateClick, onMyListingsClick }) {
  const [open, setOpen] = useState(false);
  const wrapRef = useRef(null);
  const btnRef = useRef(null);
  const menuRef = useRef(null);
  const menuId = useId();

  const close = useCallback(() => setOpen(false), []);
  const toggle = useCallback(() => setOpen((v) => !v), []);

  // Close on outside click
  useEffect(() => {
    const onDocClick = (e) => {
      if (!wrapRef.current) return;
      if (!wrapRef.current.contains(e.target)) close();
    };
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, [close]);

  // Keyboard handling
  const onKeyDown = (e) => {
    if (e.key === "Escape") {
      e.preventDefault();
      close();
      btnRef.current?.focus();
    } else if ((e.key === "ArrowDown" || e.key === "Enter" || e.key === " ") && !open) {
      e.preventDefault();
      setOpen(true);
      requestAnimationFrame(() => {
        const first = menuRef.current?.querySelector("a,button,[tabindex='0']");
        first?.focus();
      });
    }
  };

  const handleCreate = (e) => {
    if (onCreateClick) onCreateClick(e);
    close();
  };

  const handleMyListings = (e) => {
    if (onMyListingsClick) onMyListingsClick(e);
    close();
  };

  return (
    <div ref={wrapRef} className={`${styles.dropdown} ${className || ""}`}>
      <button
        ref={btnRef}
        type="button"
        className={styles.dropdownToggle}
        aria-haspopup="menu"
        aria-expanded={open ? "true" : "false"}
        aria-controls={menuId}
        onClick={toggle}
        onKeyDown={onKeyDown}
      >
        <span className={styles.fixedText}>Listings</span>
      </button>

      <ul
        id={menuId}
        ref={menuRef}
        role="menu"
        className={`${styles.dropdownMenu} ${open ? styles.showDropdown : styles.hideDropdown}`}
      >
        <li role="none">
          <Link
            role="menuitem"
            className={styles.dropdownItem}
            href="/ShuffleMaps/MyListings"
            onClick={handleMyListings}
          >
            My Listings
          </Link>
        </li>
        <li role="none">
          <Link
            role="menuitem"
            className={styles.dropdownItem}
            href="/ShuffleMaps/CreateListing"
            onClick={handleCreate}
          >
            Create Listing
          </Link>
        </li>
      </ul>
    </div>
  );
}
